export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Legal</div>
      <h1 className="mt-1 text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Privacy Policy</h1>
      <p className="mt-3 text-sm text-zinc-400">Last updated: 30 April 2026</p>

      <div className="mt-10 space-y-8 text-zinc-300 leading-relaxed text-sm">

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">1. Who We Are</h2>
          <p>HumanizeAI ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") operates the website at <strong>academichumanize.com</strong> and provides an AI text humanization service. For privacy-related enquiries, contact us at <a href="mailto:privacy@academichumanize.com" className="text-indigo-400 hover:underline">privacy@academichumanize.com</a>.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">2. Information We Collect</h2>
          <p className="mb-2">We collect the following categories of information:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Account information</strong> — your name, email address, and profile photo (provided by you or via Google OAuth).</li>
            <li><strong>Documents</strong> — text you upload or paste for AI detection and paraphrasing. These are stored in your account and used solely to provide the service.</li>
            <li><strong>Usage data</strong> — number of documents scanned, words processed, scan dates, and plan tier.</li>
            <li><strong>Payment information</strong> — payment plan, transaction ID, and payment status. We do not store card details — payments are processed by our payment provider.</li>
            <li><strong>Chat history</strong> — messages you send to the HumanizeAI Assistant chatbot.</li>
            <li><strong>Technical data</strong> — IP address, browser type, and access logs collected automatically via Cloudflare.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">3. How We Store Your Data</h2>
          <p>Your account data and documents are stored in <strong>Google Firebase Firestore</strong>, a cloud database hosted in Google's infrastructure. Authentication is handled by <strong>Firebase Authentication</strong>. Both services are governed by Google's privacy policy and comply with GDPR and other applicable data protection regulations.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">4. Cookies and Local Storage</h2>
          <p>We use browser <strong>local storage</strong> (not cookies) to save your voice and TTS preferences, chat session ID, and auto-read settings. These are stored only on your device and are never transmitted to our servers. We do not use advertising or tracking cookies.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">5. Third-Party Services</h2>
          <p className="mb-2">We use the following third-party APIs to provide our service:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Google Gemini</strong> — AI text generation, AI detection scoring, and image text extraction. Text submitted for processing may be sent to Google's API.</li>
            <li><strong>Originality.ai</strong> — When configured, used for AI content detection. Text is sent to Originality.ai's servers for analysis.</li>
            <li><strong>Humanize AI Pro</strong> — When configured, used for text paraphrasing. Text is sent to Humanize AI Pro's servers for rewriting.</li>
            <li><strong>Lemon Squeezy</strong> — Payment processing. Your billing details are handled directly by Lemon Squeezy under their own privacy policy.</li>
            <li><strong>Resend</strong> — Transactional email delivery (welcome emails, payment receipts). Your email address is shared with Resend solely for delivery purposes.</li>
            <li><strong>Cloudflare</strong> — DNS, CDN, and DDoS protection. Network-level data is processed by Cloudflare per their privacy policy.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">6. How We Use Your Information</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To create and manage your account.</li>
            <li>To process and return AI detection and paraphrasing results.</li>
            <li>To enforce plan limits and process payments.</li>
            <li>To send transactional emails (welcome, payment receipts). We do not send marketing emails without your consent.</li>
            <li>To improve service reliability and diagnose technical issues.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">7. Data Retention</h2>
          <p>Your account data is retained for as long as your account is active. Documents are retained until you delete them. If you delete your account, all associated data (documents, scans, payments, chat history) is permanently deleted from Firestore. Some anonymised usage data may be retained for analytics.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">8. Your Rights</h2>
          <p className="mb-2">Depending on your jurisdiction, you may have the right to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request deletion of your data (right to erasure) — use the "Delete Account" option in your profile.</li>
            <li>Object to or restrict processing of your data.</li>
            <li>Data portability — request an export of your data.</li>
          </ul>
          <p className="mt-2">To exercise any of these rights, contact <a href="mailto:privacy@academichumanize.com" className="text-indigo-400 hover:underline">privacy@academichumanize.com</a>.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">9. Children's Privacy</h2>
          <p>Our service is not directed at children under 13. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us and we will delete it.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last updated" date above. Continued use of the service after changes constitutes acceptance of the revised policy.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-white mb-3">11. Contact</h2>
          <p>For any privacy-related questions or requests, please email us at <a href="mailto:privacy@academichumanize.com" className="text-indigo-400 hover:underline">privacy@academichumanize.com</a>.</p>
        </section>

      </div>
    </div>
  );
}
